import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export type CatalogSection = 'tea' | 'teaware' | 'product_set'

export interface CatalogSectionSwitcherProps {
  /**
   * Текущая секция
   */
  currentSection: CatalogSection
}

/**
 * Виджет: Переключатель секций каталога (Чай / Посуда / Наборы)
 *
 * Сегмент-контрол во всю ширину экрана с плавными анимациями.
 * Активная вкладка — с акцентной заливкой и тонким градиентом.
 */
export function CatalogSectionSwitcher({ currentSection }: CatalogSectionSwitcherProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const [isTransitioning, setIsTransitioning] = useState(false)

  const handleSectionChange = (section: CatalogSection) => {
    if (section === currentSection || isTransitioning) return

    hapticFeedback.selectionChanged()
    setIsTransitioning(true)

    // Переключаем секцию через навигацию
    navigate({
      to: '/catalog/$section',
      params: { section },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        // Сбрасываем фильтры при переключении секции
        category: undefined,
      }),
    } as any)

    // Сбрасываем флаг перехода через небольшую задержку
    setTimeout(() => setIsTransitioning(false), 300)
  }

  // Позиция индикатора: 3 колонки → 0%, 33.33%, 66.66%
  const indicatorPosition = ['0%', '33.333%', '66.666%'][['tea', 'teaware', 'product_set'].indexOf(currentSection)] ?? '0%'

  const sections: { id: CatalogSection; label: string }[] = [
    { id: 'tea', label: 'Чай' },
    { id: 'teaware', label: 'Посуда' },
    { id: 'product_set', label: 'Наборы' },
  ]

  return (
    <div className="px-4 mb-4">
      <div className="relative bg-[#E8E4DD] dark:bg-dark-elevated rounded-[16px] p-1 flex">
        {/* Активный индикатор */}
        <motion.div
          className="absolute top-1 bottom-1 rounded-[12px] bg-primary dark:bg-primary-dark shadow-sm"
          initial={false}
          animate={{
            left: `calc(${indicatorPosition} + 4px)`,
            width: 'calc(33.333% - 5.33px)',
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
        />

        {/* Кнопки переключения */}
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => handleSectionChange(section.id)}
            disabled={isTransitioning}
            className={cn(
              'relative z-10 flex-1 px-2 py-3 rounded-[12px]',
              'text-sm font-semibold transition-colors duration-200',
              'touch-target min-h-[44px]',
              currentSection === section.id
                ? 'text-white'
                : 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            )}
          >
            {section.label}
          </button>
        ))}
      </div>
    </div>
  )
}
